import { useCallback, useMemo } from 'react';
import {
  Create,
  Edit,
  SaveButton,
  SelectInput,
  SelectArrayInput,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useRecordContext,
  regex,
  useGetIdentity,
} from 'react-admin';
import { useLogEffect } from '@/util/useLogEffect';
import { BSON } from 'realm-web';
import { useFormContext } from 'react-hook-form';
import { JsonInput } from '@/components/JsonInput';
import { Box, Grid } from '@mui/material';

export const entityTypes = {
  therapistSessionNotes: 'Therapist - Session Notes',
  therapistHomework: 'Therapist - Homework',
  journalEntry: 'User - Journal Entry',
};

export const entityTypesChoices = Object.entries(entityTypes).map(
  ([id, name]) => ({
    id,
    name,
  }),
);

const entityTypesByAuthorType = {
  therapist: ['therapistSessionNotes', 'therapistHomework'],
  user: ['journalEntry'],
} as const;

export const authorTypes = {
  therapist: 'Therapist',
  user: 'Journal User',
};

export const getAvailableEntityTypes = (
  authorType: keyof typeof entityTypesByAuthorType,
) => entityTypesByAuthorType[authorType];

export const authorTypesChoices = Object.entries(authorTypes).map(
  ([id, name]) => ({
    id,
    name,
  }),
);

export const AIPromptTagsEdit = ({
  id,
  onSuccess,
  isDialog,
}: {
  id?: string;
  onSuccess?: () => void;
  isDialog?: boolean;
}) => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      console.log('data', data);

      return {
        ...data,
        updatedBy: userId,
        updatedAt: new Date(),
      };
    },
    [userId],
  );

  return (
    <Edit
      id={id}
      title={isDialog ? undefined : <EditTitle />}
      mutationMode="pessimistic"
      actions={false}
      transform={transform}
      mutationOptions={{ onSuccess }}
      component={isDialog ? Box : undefined}
    >
      <AIPromptTagsEditForm />
    </Edit>
  );
};

export const AIPromptTagsCreate = ({
  onSuccess,
  isDialog,
}: {
  onSuccess?: () => void;
  isDialog?: boolean;
}) => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      console.log('data', data);

      return {
        ...data,
        _id: new BSON.ObjectId(),
        // entityType: "therapistSessionNotes",
        createdBy: userId,
        createdAt: new Date(),
      };
    },
    [userId],
  );

  return (
    <Create
      redirect={isDialog ? false : 'list'}
      transform={transform}
      title={isDialog ? undefined : <EditTitle />}
      mutationOptions={{ onSuccess }}
      component={isDialog ? Box : undefined}
    >
      <AIPromptTagsEditForm />
    </Create>
  );
};

export const EditToolbar = () => (
  <Toolbar>
    <SaveButton label="Save" />
  </Toolbar>
);

export const EditTitle = () => {
  const record = useRecordContext();

  return <span>Prompt Tag: {record ? `${record.id}` : ''}</span>;
};

const AIPromptTagsEditForm = () => {
  const record = useRecordContext();

  useLogEffect('record', record);

  return (
    <SimpleForm record={record} toolbar={<EditToolbar />}>
      <AIPromptTagsFormFields />
    </SimpleForm>
  );
};

export const tagNameRegexExpr = `^@(owner|asset|ruleset)[.][a-zA-Z0-9._-]+$`;

// New component that can access form context
const AIPromptTagsFormFields = () => {
  const { watch, formState } = useFormContext();

  // Watch specific fields
  const authorType = watch('authorType');
  const assetTypeValue = watch('assetType');

  useLogEffect('formState errors', formState.errors);

  const isRulesetTag = useMemo(() => {
    if (!assetTypeValue) {
      return false;
    }
    if (Array.isArray(assetTypeValue)) {
      return assetTypeValue.includes('documentComposerRulesets');
    }
    return assetTypeValue === 'documentComposerRulesets';
  }, [assetTypeValue]);

  const availableEntityTypes = useMemo(() => {
    const types = getAvailableEntityTypes(
      authorType as keyof typeof entityTypesByAuthorType,
    );

    const result = (types || []).map(type => ({
      id: type,
      name: entityTypes[type],
    }));

    console.log('result', result);

    return result;
  }, [authorType]);

  const authorTypeRegexType = useMemo(
    () => new RegExp(tagNameRegexExpr),
    [],
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <SelectInput
            label="Author Type"
            source="authorType"
            choices={authorTypesChoices}
            validate={[required()]}
            fullWidth
          />
        </Grid>

        <Grid item xs={6}>
          <SelectArrayInput
            label="Asset Type"
            source="assetType"
            choices={availableEntityTypes}
            validate={[required()]}
            fullWidth
          />
        </Grid>
      </Grid>

      <TextInput
        label="Tag Name"
        source="tagName"
        sx={w300px}
        validate={[
          required(),
          regex(
            authorTypeRegexType,
            `Must conform to ${tagNameRegexExpr}`,
          ),
        ]}
      />

      <TextInput label="Tag Field" source="tagField" sx={w300px} />

      <JsonInput label="Tag Expression" source="tagExpression" />
    </>
  );
};

const w300px = { minWidth: '300px' };
